/**
 * Strategy Service - Brand strategy management
 *
 * Handles business goals, funnel targets, strategy health calculations,
 * and dashboard data aggregation.
 */

import {
    doc,
    getDoc,
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getPillarsByBrand, FUNNEL_STAGES } from './pillarService';

const BRANDS_COLLECTION = 'brands';

/**
 * Default strategy structure for a brand
 */
export const DEFAULT_STRATEGY = {
    pillars: [],

    // Revenue-focused goals
    businessGoals: {
        monthlyRevenueTarget: 0,
        averageOrderValue: 0,
        customerLifetimeValue: 0,
        currentChallenge: 'new_customers' // 'new_customers' | 'repeat_purchases' | 'brand_building' | 'market_expansion'
    },

    // Derived funnel targets (calculated from business goals)
    funnelTargets: {
        requiredReach: 0,
        requiredConsideration: 0,
        requiredConversions: 0,
        conversionRate: 2.5 // Default 2.5% assumption
    },

    // Adaptive learning settings
    adaptiveSettings: {
        learningEnabled: true,
        lastOptimized: null
    },

    setupComplete: false
};

/**
 * Challenge type definitions
 */
export const CHALLENGE_TYPES = {
    new_customers: {
        label: 'Acquiring New Customers',
        description: 'Growing your customer base and reaching new audiences',
        funnelFocus: 'TOFU',
        suggestedPillars: ['Education', 'Entertainment', 'Behind the Scenes']
    },
    repeat_purchases: {
        label: 'Driving Repeat Purchases',
        description: 'Getting existing customers to buy again',
        funnelFocus: 'BOFU',
        suggestedPillars: ['Social Proof', 'Product Features', 'Offers & Promotions']
    },
    brand_building: {
        label: 'Building Brand Awareness',
        description: 'Establishing your brand in the market',
        funnelFocus: 'TOFU',
        suggestedPillars: ['Entertainment', 'Behind the Scenes', 'Education']
    },
    market_expansion: {
        label: 'Expanding to New Markets',
        description: 'Reaching new geographic or demographic markets',
        funnelFocus: 'TOFU',
        suggestedPillars: ['Education', 'Social Proof', 'Product Features']
    }
};

/**
 * Get brand strategy
 */
export const getBrandStrategy = async (brandId) => {
    try {
        const docRef = doc(db, BRANDS_COLLECTION, brandId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const brand = docSnap.data();
            return brand.strategy || DEFAULT_STRATEGY;
        }
        return DEFAULT_STRATEGY;
    } catch (error) {
        console.error('Error getting brand strategy:', error);
        throw error;
    }
};

/**
 * Initialize strategy for a brand
 */
export const initializeStrategy = async (brandId) => {
    try {
        const docRef = doc(db, BRANDS_COLLECTION, brandId);
        await updateDoc(docRef, {
            strategy: {
                ...DEFAULT_STRATEGY,
                createdAt: serverTimestamp()
            },
            updatedAt: serverTimestamp()
        });
        return DEFAULT_STRATEGY;
    } catch (error) {
        console.error('Error initializing strategy:', error);
        throw error;
    }
};

/**
 * Update brand strategy
 */
export const updateStrategy = async (brandId, strategyUpdates) => {
    try {
        const currentStrategy = await getBrandStrategy(brandId);
        const updatedStrategy = {
            ...currentStrategy,
            ...strategyUpdates,
            updatedAt: serverTimestamp()
        };

        // Recalculate funnel targets if business goals changed
        if (strategyUpdates.businessGoals) {
            updatedStrategy.funnelTargets = calculateFunnelTargets(updatedStrategy.businessGoals);
        }

        const docRef = doc(db, BRANDS_COLLECTION, brandId);
        await updateDoc(docRef, {
            strategy: updatedStrategy,
            updatedAt: serverTimestamp()
        });

        return updatedStrategy;
    } catch (error) {
        console.error('Error updating strategy:', error);
        throw error;
    }
};

/**
 * Update business goals
 */
export const updateBusinessGoals = async (brandId, businessGoals) => {
    return updateStrategy(brandId, { businessGoals });
};

/**
 * Mark strategy setup as complete
 */
export const completeStrategySetup = async (brandId) => {
    return updateStrategy(brandId, { setupComplete: true });
};

/**
 * Calculate funnel targets from business goals
 * Uses industry-standard conversion rates to work backwards from revenue
 */
export const calculateFunnelTargets = (businessGoals) => {
    const {
        monthlyRevenueTarget = 0,
        averageOrderValue = 100,
        customerLifetimeValue = 0
    } = businessGoals;

    // Calculate required conversions
    // If we have LTV, use it; otherwise use AOV
    const effectiveValue = customerLifetimeValue > 0 ? customerLifetimeValue : averageOrderValue;
    const requiredConversions = effectiveValue > 0 ? Math.ceil(monthlyRevenueTarget / effectiveValue) : 0;

    // Standard funnel conversion rates (can be customized per brand later)
    const tofuToMofu = 0.10; // 10% of reach engages (consideration)
    const mofuToBofu = 0.25; // 25% of consideration converts

    // Work backwards through funnel
    const overallConversionRate = tofuToMofu * mofuToBofu; // 2.5%
    const requiredConsideration = Math.ceil(requiredConversions / mofuToBofu);
    const requiredReach = Math.ceil(requiredConsideration / tofuToMofu);

    return {
        requiredReach,
        requiredConsideration,
        requiredConversions,
        conversionRate: overallConversionRate * 100 // As percentage
    };
};

/**
 * Calculate strategy health score (0-100)
 */
export const calculateStrategyHealth = async (brandId) => {
    try {
        const strategy = await getBrandStrategy(brandId);
        const pillars = await getPillarsByBrand(brandId);

        let score = 0;
        const factors = [];

        // Factor 1: Has pillars defined (20 points)
        if (pillars.length >= 3) {
            score += 20;
            factors.push({ name: 'Pillars Defined', score: 20, max: 20, status: 'good' });
        } else if (pillars.length > 0) {
            const points = Math.round((pillars.length / 3) * 20);
            score += points;
            factors.push({ name: 'Pillars Defined', score: points, max: 20, status: 'warning' });
        } else {
            factors.push({ name: 'Pillars Defined', score: 0, max: 20, status: 'error' });
        }

        // Factor 2: Pillar coverage balance (20 points)
        if (pillars.length > 0) {
            const weights = pillars.map(p => p.frequency?.currentWeight || 0);
            const totalWeight = weights.reduce((a, b) => a + b, 0);
            if (totalWeight > 0) {
                const maxWeight = Math.max(...weights);
                const minWeight = Math.min(...weights.filter(w => w > 0) || [0]);
                const balance = minWeight / maxWeight;
                const balanceScore = Math.round(balance * 20);
                score += balanceScore;
                factors.push({
                    name: 'Pillar Balance',
                    score: balanceScore,
                    max: 20,
                    status: balanceScore >= 15 ? 'good' : balanceScore >= 10 ? 'warning' : 'error'
                });
            } else {
                factors.push({ name: 'Pillar Balance', score: 0, max: 20, status: 'warning' });
            }
        } else {
            factors.push({ name: 'Pillar Balance', score: 0, max: 20, status: 'error' });
        }

        // Factor 3: Funnel stage coverage (20 points)
        const funnelCoverage = { TOFU: false, MOFU: false, BOFU: false };
        pillars.forEach(pillar => {
            Object.keys(FUNNEL_STAGES).forEach(stage => {
                if (pillar.funnelMapping?.[stage]?.recommended) {
                    funnelCoverage[stage] = true;
                }
            });
        });
        const coveredStages = Object.values(funnelCoverage).filter(Boolean).length;
        const funnelScore = Math.round((coveredStages / 3) * 20);
        score += funnelScore;
        factors.push({
            name: 'Funnel Coverage',
            score: funnelScore,
            max: 20,
            status: coveredStages === 3 ? 'good' : coveredStages >= 2 ? 'warning' : 'error'
        });

        // Factor 4: Business goals defined (20 points)
        const hasGoals = strategy.businessGoals?.monthlyRevenueTarget > 0;
        if (hasGoals) {
            score += 20;
            factors.push({ name: 'Revenue Goals', score: 20, max: 20, status: 'good' });
        } else {
            factors.push({ name: 'Revenue Goals', score: 0, max: 20, status: 'warning' });
        }

        // Factor 5: Setup complete (20 points)
        if (strategy.setupComplete) {
            score += 20;
            factors.push({ name: 'Setup Complete', score: 20, max: 20, status: 'good' });
        } else {
            factors.push({ name: 'Setup Complete', score: 0, max: 20, status: 'warning' });
        }

        const status = score >= 80 ? 'excellent' : score >= 60 ? 'good' : score >= 40 ? 'needs_attention' : 'critical';

        return {
            score,
            status,
            factors,
            recommendations: getHealthRecommendations(factors)
        };
    } catch (error) {
        console.error('Error calculating strategy health:', error);
        throw error;
    }
};

/**
 * Get recommendations based on health factors
 */
const getHealthRecommendations = (factors) => {
    const recommendations = [];

    factors.forEach(factor => {
        if (factor.status === 'error' || factor.status === 'warning') {
            switch (factor.name) {
                case 'Pillars Defined':
                    recommendations.push({
                        type: 'pillars',
                        priority: 'high',
                        message: 'Define at least 3 content pillars to guide your marketing strategy',
                        action: 'Add Pillars'
                    });
                    break;
                case 'Pillar Balance':
                    recommendations.push({
                        type: 'balance',
                        priority: 'medium',
                        message: 'Balance your pillar weights to avoid over-reliance on one content type',
                        action: 'Adjust Weights'
                    });
                    break;
                case 'Funnel Coverage':
                    recommendations.push({
                        type: 'funnel',
                        priority: 'high',
                        message: 'Ensure you have pillars covering all funnel stages (TOFU, MOFU, BOFU)',
                        action: 'Map Pillars'
                    });
                    break;
                case 'Revenue Goals':
                    recommendations.push({
                        type: 'goals',
                        priority: 'high',
                        message: 'Set your monthly revenue target to measure marketing effectiveness',
                        action: 'Set Goals'
                    });
                    break;
                case 'Setup Complete':
                    recommendations.push({
                        type: 'setup',
                        priority: 'medium',
                        message: 'Complete the strategy setup wizard to unlock all features',
                        action: 'Complete Setup'
                    });
                    break;
            }
        }
    });

    return recommendations.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
};

/**
 * Get pillar distribution data for dashboard
 */
export const getPillarDistribution = async (brandId) => {
    try {
        const pillars = await getPillarsByBrand(brandId);

        if (pillars.length === 0) {
            return { pillars: [], totalWeight: 0 };
        }

        const totalWeight = pillars.reduce((sum, p) => sum + (p.frequency?.currentWeight || 0), 0);

        const distribution = pillars.map(pillar => ({
            id: pillar.id,
            name: pillar.name,
            color: pillar.color,
            weight: pillar.frequency?.currentWeight || 0,
            percentage: totalWeight > 0
                ? Math.round((pillar.frequency?.currentWeight || 0) / totalWeight * 100)
                : 0,
            performance: pillar.performance
        }));

        return {
            pillars: distribution,
            totalWeight
        };
    } catch (error) {
        console.error('Error getting pillar distribution:', error);
        throw error;
    }
};

/**
 * Get funnel health data for dashboard
 */
export const getFunnelHealth = async (brandId) => {
    try {
        const pillars = await getPillarsByBrand(brandId);
        const strategy = await getBrandStrategy(brandId);

        const funnelHealth = {
            TOFU: { pillars: [], totalWeight: 0, status: 'empty' },
            MOFU: { pillars: [], totalWeight: 0, status: 'empty' },
            BOFU: { pillars: [], totalWeight: 0, status: 'empty' }
        };

        pillars.forEach(pillar => {
            Object.keys(FUNNEL_STAGES).forEach(stage => {
                if (pillar.funnelMapping?.[stage]?.recommended) {
                    funnelHealth[stage].pillars.push({
                        id: pillar.id,
                        name: pillar.name,
                        color: pillar.color,
                        weight: pillar.funnelMapping[stage].weight
                    });
                    funnelHealth[stage].totalWeight += pillar.funnelMapping[stage].weight;
                }
            });
        });

        // Determine status for each stage
        Object.keys(funnelHealth).forEach(stage => {
            const stageData = funnelHealth[stage];
            if (stageData.pillars.length === 0) {
                stageData.status = 'empty';
            } else if (stageData.pillars.length === 1) {
                stageData.status = 'warning';
            } else {
                stageData.status = 'good';
            }
        });

        return {
            stages: funnelHealth,
            targets: strategy.funnelTargets
        };
    } catch (error) {
        console.error('Error getting funnel health:', error);
        throw error;
    }
};

/**
 * Get comprehensive dashboard data
 */
export const getStrategyDashboardData = async (brandId) => {
    try {
        const [strategy, pillars, health, distribution, funnelHealth] = await Promise.all([
            getBrandStrategy(brandId),
            getPillarsByBrand(brandId),
            calculateStrategyHealth(brandId),
            getPillarDistribution(brandId),
            getFunnelHealth(brandId)
        ]);

        return {
            strategy,
            pillars,
            health,
            distribution,
            funnelHealth,
            businessGoals: strategy.businessGoals,
            funnelTargets: strategy.funnelTargets,
            setupComplete: strategy.setupComplete,
            recommendations: health.recommendations
        };
    } catch (error) {
        console.error('Error getting strategy dashboard data:', error);
        throw error;
    }
};

/**
 * Check if brand needs strategy setup
 */
export const needsStrategySetup = async (brandId) => {
    try {
        const strategy = await getBrandStrategy(brandId);
        return !strategy.setupComplete;
    } catch (error) {
        console.error('Error checking strategy setup status:', error);
        return true; // Default to needing setup
    }
};

/**
 * Get suggested pillars based on challenge type
 */
export const getSuggestedPillars = (challengeType) => {
    const challenge = CHALLENGE_TYPES[challengeType];
    return challenge?.suggestedPillars || [];
};

/**
 * Calculate progress towards revenue target
 */
export const calculateRevenueProgress = (currentRevenue, targetRevenue) => {
    if (!targetRevenue || targetRevenue <= 0) {
        return { percentage: 0, status: 'no_target' };
    }

    const percentage = Math.min(Math.round((currentRevenue / targetRevenue) * 100), 100);
    let status;

    if (percentage >= 100) {
        status = 'achieved';
    } else if (percentage >= 75) {
        status = 'on_track';
    } else if (percentage >= 50) {
        status = 'needs_attention';
    } else {
        status = 'at_risk';
    }

    return {
        percentage,
        status,
        remaining: Math.max(targetRevenue - currentRevenue, 0),
        current: currentRevenue,
        target: targetRevenue
    };
};

export default {
    getBrandStrategy,
    initializeStrategy,
    updateStrategy,
    updateBusinessGoals,
    completeStrategySetup,
    calculateFunnelTargets,
    calculateStrategyHealth,
    getPillarDistribution,
    getFunnelHealth,
    getStrategyDashboardData,
    needsStrategySetup,
    getSuggestedPillars,
    calculateRevenueProgress,
    DEFAULT_STRATEGY,
    CHALLENGE_TYPES
};
